import React from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { Send as SendIcon } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import { ToolbarButton } from '../components/ToolbarButton';
import { useToolbarAction } from '../store/hooks';
import { type ToolbarActionProps } from '../types';

export const SendFollowUpAction: React.FC<ToolbarActionProps> = ({
  context,
  compact,
  presentation,
}) => {
  const { open } = useModal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { badgeCount } = useToolbarAction('send-follow-up');
  const { toolbar, document } = useEventTracking();
  const { singular: subjectLanguage } = useSubjectLanguageVariants();

  // Only show if we have a patient context
  if (!context?.patientId) return null;

  // Build prompt with subjectLanguage
  const followUpPrompt = `Write a friendly, helpful reminder email to the ${subjectLanguage}. Keep it brief and conversational - like you're sending them a quick, supportive message. No headings, no metadata, no formal document structure. Use bullet points for important dates, numbers, and instructions where helpful, but write naturally - not everything needs to be bulleted. Keep it:
- Warm and natural - like talking to a friend
- Actionable with clear reminders they can easily follow
- Short and to the point`;

  const handleClick = () => {
    // Track with Statsig
    toolbar.actionClicked({
      action_type: 'send_follow_up',
      has_patient: !!context?.patientId,
    });

    // Track document creation intent
    document.created({
      document_type: 'follow_up',
      source: 'toolbar',
      generation_method: 'ai_generated',
    });

    // Open the document generator modal with the follow-up prompt pre-filled
    open('GENERATE_DOCUMENT', {
      title: 'Follow-Up Document',
      patientId: context.patientId,
      encounterId: context.encounterId,
      noteId: context.noteId,
      source: context.source,
      capabilitySource: 'send_followup',
      capabilityName: 'patient_follow_up_sending',
      initialPrompt: followUpPrompt,
      maxWidth: 'xl',
      fullWidth: !isMobile,
      fullHeight: !isMobile,
      fullScreen: isMobile,
    });
  };

  return (
    <ToolbarButton
      icon={SendIcon}
      label="Follow-Up"
      tooltip={`Send ${subjectLanguage} follow-up`}
      onClick={handleClick}
      compact={compact}
      badgeCount={badgeCount}
      dataAttribute="toolbar-send-follow-up"
      presentation={presentation}
      menuLabel="Send follow up"
    />
  );
};
